import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as CertIcon } from '../../../assets/icons/device-hub/medal-star.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import getFirstSubdomain from '../../../shared-functions/DomainsplitFunction';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/device-hub';

import AttachedDeviceEditForm from './AttachedDeviceEditForm';
import AddBrandDrawer from './drawers/AddBrandDrawer';
import AddConnectorRatingDrawer from './drawers/AddConnectorRatingDrawer';
import AddModelDrawer from './drawers/AddModelDrawer';
// ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout, PR #1130/#1143).
// Re-enable by uncommenting this block once backend security endpoints/fields ship.
/*
import ChargerSecurityFields from './security/ChargerSecurityFields';
import OneTimeKeyModal from './security/OneTimeKeyModal';
import SecurityEditFields from './security/SecurityEditFields';
import {
  ChargerSecurityFormState,
  DeviceSecurity,
  ModelSecurityCapability,
  SecurityEditFormState,
  buildChargerSecurityPayload,
  buildSecurityEditPatch,
  getAllowedProfiles,
  getDefaultCredentialChoice,
  getEmptyChargerSecurityForm,
  getInitialSecurityEditFormState,
  hasNoSelectableProfile,
  isProfiled,
  validateChargerSecurityForm,
  validateSecurityEditForm,
} from './security/securityProfile';
*/

import '../scss/add-device.scss';

// Options are now dynamically defined inside the component using useTranslation.

// ── Types ────────────────────────────────────────────────────────────────────────
interface ConnectorRow {
  id: number;
  currentType: string;
  connectorType: string;
  connectorFormat: string;
  maxVoltage: string;
  maxPower: string;
  maxCurrent: string;
  maxPowerOutput: string;
  connectorRating: string;
  quantity: number;
}

/** A single key/value row for the plugAndChargeParams repeatable list */
interface PlugAndChargeParameterRow {
  id: number;
  key: string;
  valueType: 'string' | 'number' | 'boolean';
  value: string | number | boolean;
}

interface AddDeviceFormState {
  deviceId: string;
  brand: string;
  model: string;
  currentType: string;
  skuNumber: string;
  installationType: string;
  networkConnectivity: string[];
  plugAndCharge: 'yes' | 'no';
  idTag: string[];
  plugAndChargeParams: PlugAndChargeParameterRow[];
  protocols: string;
  connectors: ConnectorRow[];
  maxPower: string;
  ratedPowerOutput: string;
  maxVoltage: string;
  maxCurrent: string;
  efficiencyRating: string;
  operationTemperature: string;
  weight: string;
  displayType: string;
  certifications: string[];
  firmwareVersion: string;
  diagnosticProtocolTypes: string[];
  firmwareProtocolTypes: string[];
}

// ── Sub-components ───────────────────────────────────────────────────────────────

/**
 * Info tooltip icon component
 * @returns {any} The rendered SVG icon
 */
function InfoIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="currentColor"
      style={{ color: '#aaa', flexShrink: 0 }}>
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Trash icon component for connector row deletion
 * @returns {any} The rendered SVG icon
 */
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Protocol icon component (chip/integrated circuit style)
 * @returns {any} The rendered SVG icon
 */
function ProtocolIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" />
      <line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" />
      <line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" />
      <line x1="20" y1="15" x2="23" y2="15" />
      <line x1="1" y1="9" x2="4" y2="9" />
      <line x1="1" y1="15" x2="4" y2="15" />
    </svg>
  );
}

/**
 * Generates initial state for a single connector row
 * @returns {any} Initial connector object
 */
const initialConnector = (): ConnectorRow => ({
  id: Date.now(),
  currentType: '',
  connectorType: '',
  connectorFormat: '',
  maxVoltage: '',
  maxCurrent: '',
  maxPower: '',
  maxPowerOutput: '',
  connectorRating: '',
  quantity: 1,
});

/**
 * Generates initial state for a single plugAndChargeParams row
 * @returns {PlugAndChargeParameterRow} Initial plug & charge parameter row
 */
const initialPlugAndChargeParameter = (): PlugAndChargeParameterRow => ({
  id: Date.now() + Math.random(),
  key: '',
  valueType: 'string',
  value: '',
});

/**
 * Generates initial state for the entire add device form
 * @returns {any} Initial form state
 */
const initialForm = (): AddDeviceFormState => ({
  deviceId: '',
  brand: '',
  model: '',
  currentType: '',
  skuNumber: '',
  installationType: '',
  networkConnectivity: [],
  plugAndCharge: 'no',
  idTag: [],
  plugAndChargeParams: [],
  protocols: 'OCPP 1.6',
  connectors: [initialConnector()],
  ratedPowerOutput: '',
  maxVoltage: '',
  maxCurrent: '',
  maxPower: '',
  efficiencyRating: '',
  operationTemperature: '',
  weight: '',
  displayType: '',
  certifications: [],
  firmwareVersion: '',
  diagnosticProtocolTypes: [],
  firmwareProtocolTypes: [],
});

interface ConnectorRatingOption {
  value: string;
  label: string;
  currentType?: string;
  name?: string;
  capacityKW?: string;
  labelNode?: React.ReactNode;
}

/**
 * AddDevice component - Full page view for adding a new EV charging device.
 * @returns {any} The rendered AddDevice page.
 */
function AddDevice() {
  const { t } = useTranslation('deviceHub');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [form, setForm] = useState<AddDeviceFormState>(initialForm);
  const [loading, setLoading] = useState(isEdit);
  // Text being typed in the ID tag box before it is committed as a chip
  const [idTagDraft, setIdTagDraft] = useState('');
  // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
  // PR #1130/#1143). Re-enable by uncommenting this block once backend security
  // endpoints/fields ship.
  /*
  // Security section (create only). The capability is null for unprofiled/global models.
  const [chargerSecurity, setChargerSecurity] =
    useState<ChargerSecurityFormState>(getEmptyChargerSecurityForm);
  const [modelCapability, setModelCapability] = useState<ModelSecurityCapability | null>(null);
  const [modelCapabilityLoaded, setModelCapabilityLoaded] = useState(false);
  // Platform-generated AuthorizationKey, held only while the one-time modal is open
  const [oneTimeKey, setOneTimeKey] = useState<string | null>(null);
  const allowedSecurityProfiles = getAllowedProfiles(modelCapability);
  const noSecurityProfileAvailable = hasNoSelectableProfile(modelCapability);
  */

  // Edit mode: the raw device fetched by id, used to detect attached chargers (and the
  // restricted attached-settings form).
  const [rawDevice, setRawDevice] = useState<any>(null);
  /*
  const deviceSecurity: DeviceSecurity | null = rawDevice?.security ?? null;
  */
  const isAttachedDevice = Boolean(isEdit && rawDevice?.attachedBy);
  /*
  const [securityEditState, setSecurityEditState] = useState<SecurityEditFormState>(() =>
    getInitialSecurityEditFormState(null),
  );
  const [showSecurityEditErrors, setShowSecurityEditErrors] = useState(false);

  // Seed the Security edit form once the device (and its security object) has loaded.
  useEffect(() => {
    if (deviceSecurity) {
      setSecurityEditState(getInitialSecurityEditFormState(deviceSecurity));
    }
  }, [deviceSecurity]);
  */

  // Best-effort protocol names for the "Using the device model's protocols" hint (edit only).
  const [modelFirmwareProtocols, setModelFirmwareProtocols] = useState<string[]>([]);
  const [modelDiagnosticProtocols, setModelDiagnosticProtocols] = useState<string[]>([]);
  useEffect(() => {
    const serialNumber = rawDevice?.serialNumber;
    if (!isEdit || !serialNumber) return;
    deviceApi
      .fetchSupportedProtocols(serialNumber, 'firmware')
      .then((response) => setModelFirmwareProtocols(response?.data?.protocols || response?.data?.data?.protocols || []))
      .catch(() => setModelFirmwareProtocols([]));
    deviceApi
      .fetchSupportedProtocols(serialNumber, 'diagnostics')
      .then((response) => setModelDiagnosticProtocols(response?.data?.protocols || response?.data?.data?.protocols || []))
      .catch(() => setModelDiagnosticProtocols([]));
  }, [isEdit, rawDevice?.serialNumber]);

  /**
   * The "Using the device model's protocols" hint, naming the model's protocols when known.
   * @param {string[]} modelProtocols - The model's protocols for this feature, if fetched
   * @returns {string} The hint text
   */
  const protocolHint = (modelProtocols: string[]): string =>
    modelProtocols.length > 0
      ? t('usingModelProtocolsNamed', { protocols: modelProtocols.join(', ') })
      : t('usingModelProtocols');

  // Dynamic option lists
  const CURRENT_TYPE_OPTIONS = React.useMemo(
    () => [
      { value: 'ac', label: t('ac') },
      { value: 'dc', label: t('dc') },
      { value: 'hybrid', label: t('hybrid') },
    ],
    [t],
  );

  const INSTALLATION_TYPE_OPTIONS = React.useMemo(
    () => [
      { value: 'wall', label: t('wallMounted') },
      { value: 'pedestal', label: t('pedestal') },
    ],
    [t],
  );

  const NETWORK_OPTIONS = React.useMemo(
    () => [
      { value: '4g', label: '4G/LTE' },
      { value: 'wifi', label: 'Wi-Fi' },
      { value: 'eth', label: t('ethernet') },
    ],
    [t],
  );

  // ! Temporarily hided 2.0.1 and 2.1
  // const PROTOCOL_OPTIONS = React.useMemo(() => ['OCPP 1.6', 'OCPP 2.0.1', 'OCPP 2.1'], []);
  const PROTOCOL_OPTIONS = React.useMemo(() => ['OCPP 1.6'], []);

  /** Options for diagnosis and firmware protocol type checkboxes */
  const TRANSFER_PROTOCOL_OPTIONS = React.useMemo(
    () => [
      { label: 'HTTPS', value: 'HTTPS' },
      { label: 'FTPS', value: 'FTPS' },
      { label: 'HTTP', value: 'HTTP' },
      { label: 'FTP', value: 'FTP' },
    ],
    [],
  );

  const CONNECTOR_TYPE_OPTIONS = React.useMemo(
    () => [
      { value: 'CCS1', label: 'CCS1' },
      { value: 'CCS2', label: 'CCS2' },
      { value: 'Type2', label: 'Type2' },
      { value: 'Type1', label: 'Type1' },
      { value: 'CHAdeMO', label: 'CHAdeMO' },
      { value: 'NACS', label: 'NACS' },
      { value: 'J1772', label: 'J1772' },
    ],
    [],
  );

  const CONNECTOR_FORMAT_OPTIONS = React.useMemo(
    () => [
      { value: 'socket', label: t('socket') },
      { value: 'cable', label: t('cable') },
    ],
    [t],
  );

  const DISPLAY_TYPE_OPTIONS = React.useMemo(
    () => [
      { value: 'none', label: t('none') },
      { value: 'lcd', label: 'LCD' },
      { value: 'led', label: 'LED' },
    ],
    [t],
  );

  const CERTIFICATIONS = React.useMemo(() => ['CE', 'UL', 'BIS', 'TUV', 'RoHS'], []);

  /** Options for the plugAndChargeParams value-type select dropdown */
  const VALUE_TYPE_OPTIONS = React.useMemo(
    () => [
      { value: 'string', label: t('valueTypeString') },
      { value: 'number', label: t('valueTypeNumber') },
      { value: 'boolean', label: t('valueTypeBoolean') },
    ],
    [t],
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // Dynamic options state
  const [brandOptions, setBrandOptions] = useState<
    { value: string; label: string; labelNode?: React.ReactNode }[]
  >([]);
  const [modelOptions, setModelOptions] = useState<
    { value: string; label: string; labelNode?: React.ReactNode }[]
  >([]);
  const [connectorRatingOptions, setConnectorRatingOptions] = useState<ConnectorRatingOption[]>([]);

  // Modals state
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);

  const [modelsLoading, setModelsLoading] = useState<boolean>(false);

  /**
   * Fetches active brand list from API and updates brand options state.
   * @returns {any} []>} A promise that resolves to brand options array.
   */
  const loadBrands = () => {
    return deviceApi
      .fetchBrands({ paginate: false, status: ['approved', 'pending'] })
      .then((response) => {
        const data = response.data?.results || response.data || [];
        const brands = data.map((brand: any) => {
          const isGlobal = !!brand.source;
          return {
            value: brand._id,
            label: brand.name,
            labelNode: (
              <div className="nd-brand-option">
                <span>{brand.name}</span>
                <span className={`nd-badge ${isGlobal ? 'global' : 'local'}`}>
                  {isGlobal ? t('global') : t('local')}
                </span>
              </div>
            ),
          };
        });
        setBrandOptions(brands);
        return brands;
      })
      .catch((error) => {
        console.error('Failed to fetch brands:', error);
        return [];
      });
  };

  /**
   * Fetches active models list for a specific brand.
   * @param {string} [brandIdOrName] - The brand identifier or name.
   * @param {Array<{ value: string; label: string }>} [overrideBrands] - Optional override brand list.
   * @returns {Promise<{ value: string; label: string }[]>} A promise resolving to the model options array.
   */
  const loadModels = (
    brandIdOrName?: string,
    overrideBrands?: { value: string; label: string; labelNode?: React.ReactNode }[],
  ): Promise<{ value: string; label: string; labelNode?: React.ReactNode }[]> => {
    if (!brandIdOrName) {
      setModelOptions([]);
      return Promise.resolve([]);
    }

    const availableBrands =
      overrideBrands && overrideBrands.length > 0 ? overrideBrands : brandOptions;

    const selectedBrand = availableBrands.find(
      (brand) =>
        brand.value === brandIdOrName || brand.label.toLowerCase() === brandIdOrName.toLowerCase(),
    );

    if (!selectedBrand) {
      setModelOptions([]);
      return Promise.resolve([]);
    }

    // Clear old models immediately while fetching new models
    setModelOptions([]);
    setModelsLoading(true);

    return deviceApi
      .fetchModels({
        brandName: selectedBrand.label,
        paginate: false,
        status: ['approved', 'pending'],
      })
      .then((response) => {
        const data = response.data?.results || response.data || [];

        // const models = data.map((model: any) => ({
        //   value: model._id,
        //   label: model.name,
        // }));

        const models = data.map((model: any) => {
          const isGlobal = !!model.source;
          return {
            value: model._id,
            label: model.name,
            labelNode: (
              <div className="nd-brand-option">
                <span>{model.name}</span>
                <span className={`nd-badge ${isGlobal ? 'global' : 'local'}`}>
                  {isGlobal ? t('global') : t('local')}
                </span>
              </div>
            ),
          };
        });

        setModelOptions(models);
        setModelsLoading(false);
        return models;
      })
      .catch((error) => {
        console.error('Failed to fetch models:', error);
        setModelOptions([]);
        setModelsLoading(false);
        return [];
      });
  };
  /**
   * Fetches dynamic connector ratings from the API and updates options state.
   * @returns {Promise<ConnectorRatingOption[]>} A promise that resolves to rating options array.
   */
  const loadConnectorRatings = (): Promise<ConnectorRatingOption[]> => {
    return deviceApi
      .connecterRateList({ paginate: false })
      .then((response) => {
        const data = response?.data?.results || response?.data || [];
        const options: ConnectorRatingOption[] = data.map((item: any) => {
          const isGlobal = !!item?.source;

          if (item?.value && item?.label) {
            return {
              value: item?.value,
              label: item?.label,
              currentType: item?.currentType,
              name: item?.name,
              capacityKW: item?.capacityKW !== undefined ? String(item.capacityKW) : undefined,
              labelNode: (
                <div className="nd-brand-option">
                  <span>{item?.label}</span>
                  <span className={`nd-badge ${isGlobal ? 'global' : 'local'}`}>
                    {isGlobal ? t('global') : t('local')}
                  </span>
                </div>
              ),
            };
          }
          const maxPower = item?.capacityKW !== undefined ? String(item.capacityKW) : '';
          const unit = item?.unit || 'kW';
          const label =
            item?.name ||
            (item?.currentType ? `${item.currentType} ${maxPower} ${unit}` : `${maxPower} ${unit}`);
          const value = item?._id || item?.id || '';
          const { currentType, name } = item;
          return {
            value,
            label,
            currentType,
            name,
            capacityKW: maxPower,
            labelNode: (
              <div className="nd-brand-option">
                <span>{label}</span>
                <span className={`nd-badge ${isGlobal ? 'global' : 'local'}`}>
                  {isGlobal ? t('global') : t('local')}
                </span>
              </div>
            ),
          };
        });

        setConnectorRatingOptions(options);
        return options;
      })
      .catch((error) => {
        console.error('Failed to fetch connector ratings:', error);
        setConnectorRatingOptions([]);
        return [];
      });
  };

  useEffect(() => {
    // Load brands and connector ratings first, then if editing, fetch device data
    Promise.all([loadBrands(), loadConnectorRatings()]).then(([brands, ratingOptions]) => {
      if (isEdit) {
        setLoading(true);
        deviceApi
          .fetchDeviceById(id)
          .then((response) => {
            const device = response.data;
            setRawDevice(device);

            // 1. Match Brand by brandName or brandId
            const matchedBrand = brands.find(
              (b: any) =>
                (device.brandName && b.label.toLowerCase() === device.brandName.toLowerCase()) ||
                (device.brandId &&
                  b.value ===
                    (typeof device.brandId === 'object' ? device.brandId?._id : device.brandId)),
            );
            const brandValue = matchedBrand ? matchedBrand.value : '';
            const brandNameValue = matchedBrand ? matchedBrand.label : device.brandName || '';

            // 2. Load Models for the matched brand to match modelName or modelId
            const modelPromise =
              brandValue || brandNameValue
                ? loadModels(brandValue || brandNameValue, brands)
                : Promise.resolve([]);

            modelPromise.then((models) => {
              // 3. Match Model by modelName or modelId
              const matchedModel = models.find(
                (m: any) =>
                  (device.modelName && m.label.toLowerCase() === device.modelName.toLowerCase()) ||
                  (device.modelId &&
                    m.value ===
                      (typeof device.modelId === 'object' ? device.modelId?._id : device.modelId)),
              );
              let modelValue = '';
              if (matchedModel) {
                modelValue = matchedModel.value;
              } else if (typeof device.modelId === 'object') {
                modelValue = device.modelId?._id || '';
              } else {
                modelValue = device.modelId || '';
              }

              // 4. Group Connectors and match rating value
              const groupedConnectors = device.connectorInformation?.length
                ? (Object.values(
                    device.connectorInformation.reduce(
                      (accumulator: Record<string, ConnectorRow>, connector: any) => {
                        let ratingValue = '';
                        let matchedRating: ConnectorRatingOption | undefined;

                        if (
                          connector.connectorRating &&
                          typeof connector.connectorRating === 'object'
                        ) {
                          const ratingObject = connector.connectorRating;
                          matchedRating = ratingOptions.find((option: ConnectorRatingOption) => {
                            const nameMatch =
                              option.name &&
                              ratingObject.name &&
                              option.name.toLowerCase() === ratingObject.name.toLowerCase();
                            const typeMatch =
                              option.currentType &&
                              ratingObject.currentType &&
                              option.currentType.toLowerCase() ===
                                ratingObject.currentType.toLowerCase();
                            const capMatch =
                              option.capacityKW !== undefined &&
                              ratingObject.capacityKW !== undefined &&
                              String(option.capacityKW) === String(ratingObject.capacityKW);
                            return nameMatch && typeMatch && capMatch;
                          });

                          if (!matchedRating && ratingObject._id) {
                            matchedRating = ratingOptions.find(
                              (option: ConnectorRatingOption) => option.value === ratingObject._id,
                            );
                          }

                          if (matchedRating) {
                            ratingValue = matchedRating.value;
                          } else {
                            ratingValue = ratingObject._id || ratingObject.name || '';
                          }
                        } else if (connector.connectorRating) {
                          ratingValue = connector.connectorRating;
                          matchedRating = ratingOptions.find(
                            (option: ConnectorRatingOption) =>
                              option.value === ratingValue || option.label === ratingValue,
                          );
                          if (matchedRating) {
                            ratingValue = matchedRating.value;
                          }
                        }

                        const key = JSON.stringify({
                          currentType: connector.currentType,
                          connectorType: connector.connectorType,
                          connectorFormat: connector.connectorFormat,
                          maxVoltage: connector.maxVoltage,
                          maxCurrent: connector.maxCurrent,
                          maxPowerOutput: connector.maxPowerOutput,
                          connectorRating: ratingValue,
                        });

                        if (!accumulator[key]) {
                          accumulator[key] = {
                            id: Date.now() + Math.random(),
                            currentType: connector.currentType?.toLowerCase() || '',
                            connectorType: connector.connectorType || '',
                            connectorFormat: connector.connectorFormat || '',
                            maxVoltage: connector.maxVoltage?.toString() || '',
                            maxPower: connector.maxPower?.toString() || '',
                            maxCurrent: connector.maxCurrent?.toString() || '',
                            maxPowerOutput: connector.maxPowerOutput?.toString() || '',
                            connectorRating: ratingValue,
                            quantity: 1,
                          };
                        } else {
                          accumulator[key].quantity += 1;
                        }

                        return accumulator;
                      },
                      {},
                    ),
                  ) as ConnectorRow[])
                : [initialConnector()];

              if (device) {
                // Mapping network connectivity back to form values
                let selectedNetworks: string[] = [];
                if (Array.isArray(device.networkConnectivity)) {
                  selectedNetworks = device.networkConnectivity.map((value: string) => {
                    const normalized = value.toLowerCase();
                    if (normalized === '4g/lte' || normalized === '4g') return '4g';
                    if (normalized === 'wifi') return 'wifi';
                    if (normalized === 'ethernet' || normalized === 'eth') return 'eth';
                    return value;
                  });
                } else if (typeof device.networkConnectivity === 'string') {
                  const normalized = device.networkConnectivity.toLowerCase();
                  if (normalized === '4g/lte' || normalized === '4g') selectedNetworks = ['4g'];
                  else if (normalized === 'wifi') selectedNetworks = ['wifi'];
                  else if (normalized === 'ethernet' || normalized === 'eth')
                    selectedNetworks = ['eth'];
                  else if (device.networkConnectivity)
                    selectedNetworks = [device.networkConnectivity];
                }

                const protocolValue = Array.isArray(device.protocolSupport)
                  ? device.protocolSupport[0]
                  : device.protocolSupport;

                setForm({
                  deviceId: device.serialNumber || '',
                  brand: brandValue,
                  model: modelValue,
                  currentType: device.currentType ? device.currentType.toLowerCase() : '',
                  skuNumber: device.skuNumber || '',
                  installationType: device.installationType || '',
                  networkConnectivity: selectedNetworks,
                  plugAndCharge: device.plugAndCharge ? 'yes' : 'no',
                  // De-duplicated because each tag renders as a chip keyed by its value
                  idTag: Array.isArray(device.idTag) ? Array.from(new Set(device.idTag)) : [],
                  plugAndChargeParams: Array.isArray(device.plugAndChargeParams)
                    ? device.plugAndChargeParams.map((parameter: any, index: number) => ({
                        id: Date.now() + index,
                        key: parameter.key ?? '',
                        valueType: parameter.valueType ?? 'string',
                        value: parameter.value,
                      }))
                    : [],
                  protocols:
                    PROTOCOL_OPTIONS.find(
                      (opt) => opt.toLowerCase() === protocolValue?.toLowerCase(),
                    ) ||
                    protocolValue ||
                    '',
                  connectors: groupedConnectors,
                  ratedPowerOutput:
                    device.specifications?.powerOutput?.toString() ||
                    device.specifications?.ratedPowerOutput?.toString() ||
                    '',
                  maxVoltage: device.specifications?.maxVoltage || '',
                  maxPower: device.specifications?.maxPower || '',
                  maxCurrent: device.specifications?.maxCurrent || '',
                  efficiencyRating: device.specifications?.efficiencyRating || '',
                  operationTemperature: device.specifications?.operationTemperature || '',
                  weight: device.specifications?.weight || '',
                  displayType: device.specifications?.displayType || '',
                  certifications: (
                    device.certifications ||
                    device.specifications?.certifications ||
                    []
                  ).map(
                    (c: string) =>
                      CERTIFICATIONS.find((opt) => opt.toLowerCase() === c.toLowerCase()) || c,
                  ),
                  firmwareVersion:
                    device.firmwareVersion || device.specifications?.firmwareVersion || '',
                  diagnosticProtocolTypes: Array.isArray(device.diagnosticProtocolTypes)
                    ? device.diagnosticProtocolTypes
                    : [],
                  firmwareProtocolTypes: Array.isArray(device.firmwareProtocolTypes)
                    ? device.firmwareProtocolTypes
                    : [],
                });
              }
            });
          })
          .catch((error) => {
            console.error('Failed to fetch device data:', error);
            dispatch(
              setNotificationMessage({
                status: true,
                type: 'error',
                message: t('failedToFetchDevice'),
                code: 0,
              }),
            );
          })
          .finally(() => setLoading(false));
      }
    });
  }, [dispatch, id, isEdit]);

  // Fetch models whenever brand selection changes
  useEffect(() => {
    if (!form.brand) {
      setModelOptions([]);
      return;
    }

    loadModels(form.brand);
  }, [form.brand, brandOptions]);

  // Auto-populate diagnosis/firmware protocol types from the model when the model changes
  // (non-edit only)
  useEffect(() => {
    if (isEdit) return;
    // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
    // PR #1130/#1143). Re-enable by uncommenting this block once backend security
    // endpoints/fields ship.
    /*
    setModelCapability(null);
    setModelCapabilityLoaded(false);
    setChargerSecurity(getEmptyChargerSecurityForm());
    */
    if (!form.model) return;

    deviceApi
      .fetchModelById(form.model)
      .then((response) => {
        const modelData = response?.data?.results?.[0] || response?.data || {};
        /*
        // Global models carry no tenant capability record, so they count as not profiled.
        const capability: ModelSecurityCapability | null =
          !modelData.source && isProfiled(modelData.security) ? modelData.security : null;
        const allowed = getAllowedProfiles(capability);
        // Prefill the most secure profile the model supports; unprofiled models must pick 0/1 deliberately.
        // A model that only lists profile 3 (later phase) has nothing selectable yet.
        const defaultProfile = capability ? (allowed[allowed.length - 1] ?? '') : '';
        setModelCapability(capability);
        setModelCapabilityLoaded(true);
        setChargerSecurity({
          ...getEmptyChargerSecurityForm(),
          expectedSecurityProfile: defaultProfile,
          credentialSource: getDefaultCredentialChoice(defaultProfile),
        });
        */
        setForm((previous) => ({
          ...previous,
          diagnosticProtocolTypes:
            Array.isArray(modelData.diagnosticProtocolTypes) &&
            modelData.diagnosticProtocolTypes.length > 0
              ? modelData.diagnosticProtocolTypes
              : previous.diagnosticProtocolTypes,
          firmwareProtocolTypes:
            Array.isArray(modelData.firmwareProtocolTypes) &&
            modelData.firmwareProtocolTypes.length > 0
              ? modelData.firmwareProtocolTypes
              : previous.firmwareProtocolTypes,
        }));
      })
      .catch(() => {
        // Protocols can be set manually.
        // setModelCapabilityLoaded(true); — SECURITY PROFILES DISABLED, see above
      });
  }, [form.model, isEdit]);

  /**
   * Collects the committed ID tag chips plus any text still sitting in the input
   * box, so a tag typed without pressing Enter is still saved.
   * @returns {string[]} Trimmed, de-duplicated, non-empty ID tags
   */
  const collectIdTags = (): string[] =>
    Array.from(
      new Set([...form.idTag, idTagDraft].map((tag) => tag.trim()).filter((tag) => tag !== '')),
    );

  /**
   * Save device (create or update)
   */
  const handleSave = () => {
    // Validate all required fields
    const newErrors: Record<string, boolean> = {};
    let hasValidationError = false;

    // 1. Device ID
    if (!form.deviceId) {
      newErrors.deviceId = true;
      hasValidationError = true;
    }
    // 2. Brand
    if (!form.brand) {
      newErrors.brand = true;
      hasValidationError = true;
    }
    // 3. Model
    if (!form.model) {
      newErrors.model = true;
      hasValidationError = true;
    }
    // 4. Current Type
    if (!form.currentType) {
      newErrors.currentType = true;
      hasValidationError = true;
    }
    // 5. Protocols (Must select at least one protocol)
    if (!form.protocols || form.protocols.length === 0) {
      newErrors.protocols = true;
      hasValidationError = true;
    }
    // 6. Network Connectivity
    if (!form.networkConnectivity || form.networkConnectivity.length === 0) {
      newErrors.networkConnectivity = true;
      hasValidationError = true;
    }
    // 7. Plug & Charge
    if (!form.plugAndCharge) {
      newErrors.plugAndCharge = true;
      hasValidationError = true;
    }
    // 8. Rated Power Output
    if (!form.ratedPowerOutput) {
      newErrors.ratedPowerOutput = true;
      hasValidationError = true;
    }
    // Protocol checks — empty is allowed on edit (falls back to the model's protocols)
    if (!isEdit) {
      if (!form.diagnosticProtocolTypes || form.diagnosticProtocolTypes.length === 0) {
        newErrors.diagnosticProtocolTypes = true;
        hasValidationError = true;
      }
      if (!form.firmwareProtocolTypes || form.firmwareProtocolTypes.length === 0) {
        newErrors.firmwareProtocolTypes = true;
        hasValidationError = true;
      }
    }
    // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
    // PR #1130/#1143). Re-enable by uncommenting this block once backend security
    // endpoints/fields ship.
    /*
    // Security (create: edited here; edit: the Security section above; attached chargers use
    // their own restricted form instead of this one)
    if (!isEdit && form.model && noSecurityProfileAvailable) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: t('noSecurityProfileAvailableInPhase'),
          code: 0,
        }),
      );
      return;
    }
    if (!isEdit) {
      const securityErrors = validateChargerSecurityForm(chargerSecurity, allowedSecurityProfiles);
      if (Object.keys(securityErrors).length > 0) {
        Object.assign(newErrors, securityErrors);
        hasValidationError = true;
      }
    } else {
      // Also applies to a charger onboarded before security profiles existed, which has no
      // stored security object yet — the Security section still shows, defaulted to Profile 0.
      setShowSecurityEditErrors(true);
      const securityEditErrors = validateSecurityEditForm(deviceSecurity, securityEditState);
      if (Object.keys(securityEditErrors).length > 0) {
        hasValidationError = true;
      }
    }
    */
    // 9. Connector row fields
    form.connectors.forEach((c) => {
      if (!c.currentType) {
        newErrors[`currentType-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.connectorType) {
        newErrors[`connectorType-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.connectorFormat) {
        newErrors[`connectorFormat-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.maxVoltage) {
        newErrors[`maxVoltage-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.maxCurrent) {
        newErrors[`maxCurrent-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.maxPower) {
        newErrors[`maxPower-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.connectorRating) {
        newErrors[`connectorRating-${c.id}`] = true;
        hasValidationError = true;
      }
    });

    if (hasValidationError) {
      setErrors(newErrors);
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: t('requiredFieldsError'),
          code: 0,
        }),
      );
      return;
    }

    // Plug & Charge conditional requirement: when plugAndCharge is true, at least one of
    // idTag or plugAndChargeParams must be provided (mirrors server-side validation).
    if (form.plugAndCharge === 'yes') {
      const hasIdTag = collectIdTags().length > 0;
      const hasParameters = form.plugAndChargeParams.length > 0;

      if (!hasIdTag && !hasParameters) {
        setErrors((previousErrors) => ({ ...previousErrors, plugAndChargeRequirement: true }));
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message: t('plugAndChargeValidationError'),
            code: 0,
          }),
        );
        return;
      }

      const parameterErrors: Record<string, boolean> = {};
      let parameterHasError = false;
      form.plugAndChargeParams.forEach((parameter) => {
        if (!parameter.key.trim()) {
          parameterErrors[`paramKey-${parameter.id}`] = true;
          parameterHasError = true;
        }
        if (
          parameter.valueType !== 'boolean' &&
          (parameter.value === '' || parameter.value === null || parameter.value === undefined)
        ) {
          parameterErrors[`paramValue-${parameter.id}`] = true;
          parameterHasError = true;
        }
      });

      if (parameterHasError) {
        setErrors((previousErrors) => ({ ...previousErrors, ...parameterErrors }));
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message: t('requiredFieldsError'),
            code: 0,
          }),
        );
        return;
      }
    }

    const totalConnectors = form.connectors.reduce((sum, connector) => sum + connector.quantity, 0);
    if (totalConnectors > 4) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: t('limitConnectorError'),
          code: 0,
        }),
      );
      return;
    }

    // Mapping network connectivity values to match backend Joi valid values (all lowercase as required by the backend)
    const networkMap: Record<string, string> = {
      '4g': '4g/lte',
      wifi: 'wifi',
      eth: 'ethernet',
    };

    const selectedBrandName = brandOptions.find((b) => b.value === form.brand)?.label || form.brand;
    const selectedModelName = modelOptions.find((m) => m.value === form.model)?.label || form.model;

    /**
     * Casts a plugAndChargeParams row's raw value to the type implied by its valueType,
     * so the API never receives e.g. a stringified number when valueType is "number".
     * @param {'string' | 'number' | 'boolean'} valueType - The selected value type
     * @param {string | number | boolean} value - The raw row value
     * @returns {string | number | boolean} The value cast to the matching type
     */
    const castParameterValue = (
      valueType: 'string' | 'number' | 'boolean',
      value: string | number | boolean,
    ): string | number | boolean => {
      if (valueType === 'number') return Number(value);
      if (valueType === 'boolean') return Boolean(value);
      return String(value);
    };

    // 1. Build the raw payload with all potential fields according to the schema
    const rawPayload: any = {
      serialNumber: form.deviceId,
      brandName: selectedBrandName,
      modelName: selectedModelName,
      skuNumber: form.skuNumber,
      tenantName: getFirstSubdomain() || '',
      currentType: form.currentType
        ? (form.currentType.toUpperCase() as 'AC' | 'DC' | 'HYBRID')
        : undefined,
      installationType: form.installationType,
      networkConnectivity: form.networkConnectivity.map((value) => networkMap[value] || value),
      plugAndCharge: form.plugAndCharge === 'yes',
      idTag: form.plugAndCharge === 'yes' ? collectIdTags() : undefined,
      plugAndChargeParams:
        form.plugAndCharge === 'yes' && form.plugAndChargeParams.length > 0
          ? form.plugAndChargeParams.map((parameter) => ({
              key: parameter.key.trim(),
              valueType: parameter.valueType,
              value: castParameterValue(parameter.valueType, parameter.value),
            }))
          : undefined,
      protocolSupport: form.protocols.length > 0 ? form.protocols.toLowerCase() : undefined,
      connectorInformation: form.connectors.flatMap((c) => {
        const ratingOpt = connectorRatingOptions.find(
          (opt) => opt.value === c.connectorRating || opt.label === c.connectorRating,
        );
        const connectorRatingObject = ratingOpt
          ? {
              name: ratingOpt.name || '',
              currentType: ratingOpt.currentType || '',
              capacityKW: ratingOpt.capacityKW ? String(ratingOpt.capacityKW) : '',
            }
          : undefined;

        const connectorObject = {
          currentType: c.currentType?.toUpperCase(),
          connectorType: c.connectorType,
          connectorFormat: c.connectorFormat,
          maxVoltage: c.maxVoltage ? Number(c.maxVoltage) : undefined,
          maxCurrent: c.maxCurrent ? Number(c.maxCurrent) : undefined,
          maxPower: c.maxPower ? Number(c.maxPower) : undefined,
          connectorRating: connectorRatingObject,
          unit: 'kW',
        };
        // If quantity > 1, the backend expects multiple items in the array for each physical connector
        return Array(c.quantity).fill(connectorObject);
      }),
      specifications: {
        powerOutput: form.ratedPowerOutput ? Number(form.ratedPowerOutput) : undefined,
        maxVoltage: form.maxVoltage,
        maxCurrent: form.maxCurrent,
        efficiencyRating: form.efficiencyRating,
        operationTemperature: form.operationTemperature,
        weight: form.weight,
        displayType: form.displayType,
      },
      firmwareVersion: form.firmwareVersion,
      certifications:
        form.certifications.length > 0
          ? form.certifications.map((c) => c.toLowerCase())
          : undefined,
      diagnosticProtocolTypes:
        form.diagnosticProtocolTypes.length > 0 ? form.diagnosticProtocolTypes : undefined,
      firmwareProtocolTypes:
        form.firmwareProtocolTypes.length > 0 ? form.firmwareProtocolTypes : undefined,
      // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. Do not send a `security` key
      // until backend support ships. Re-enable by uncommenting this once it does.
      // security: isEdit
      //   ? buildSecurityEditPatch(deviceSecurity, securityEditState)
      //   : buildChargerSecurityPayload(chargerSecurity),
    };

    /**
     * Recursively removes undefined, null, empty strings, empty arrays, and empty objects.
     * This ensures the payload strictly matches the "only send fields with values" requirement.
     * @param {any} value - The value
     */
    const clean = (value: any): any => {
      // Handle Arrays
      if (Array.isArray(value)) {
        const cleanedArray = value
          .map(clean)
          .filter((item) => item !== undefined && item !== null && item !== '');
        return cleanedArray.length > 0 ? cleanedArray : undefined;
      }
      // Handle Objects
      if (value && typeof value === 'object' && !(value instanceof Date)) {
        const cleanedObject = Object.fromEntries(
          Object.entries(value)
            .map(([key, item]) => [key, clean(item)])
            .filter(([, item]) => item !== undefined && item !== null && item !== ''),
        );
        return Object.keys(cleanedObject).length > 0 ? cleanedObject : undefined;
      }
      // Handle primitive values
      return value === '' || value === null ? undefined : value;
    };

    const cleanPayload = clean(rawPayload) || {};
    if (isEdit) {
      // clean() drops empty arrays entirely, but on a PATCH an omitted key means "leave it
      // unchanged" — an explicit [] is how the user asks to fall back to the model's protocols.
      cleanPayload.diagnosticProtocolTypes = form.diagnosticProtocolTypes;
      cleanPayload.firmwareProtocolTypes = form.firmwareProtocolTypes;
    }

    setLoading(true);
    const apiCall = isEdit
      ? deviceApi.updateDevice(id || '', cleanPayload)
      : deviceApi.addDevice(cleanPayload);

    apiCall
      .then(() => {
        // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. Re-enable alongside the
        // Security section once backend security endpoints/fields ship.
        /*
        if (!isEdit) {
          // Drop any entered key from state as soon as it has been sent.
          setChargerSecurity((previous) => ({ ...previous, authorizationKey: '' }));
        } else {
          setSecurityEditState((previous) => ({ ...previous, enteredKey: '' }));
        }
        const generatedKey = response?.data?.authorizationKey;
        if (generatedKey) {
          setOneTimeKey(generatedKey);
        } else {
          setShowSuccess(true);
        }
        */
        setShowSuccess(true);
        window.dispatchEvent(
          new CustomEvent('ev-device-updated', {
            detail: { deviceId: id },
          }),
        );
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message: error?.response?.data?.message || t('somethingWentWrong'),
            code: 0,
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // ── Field helpers ──────────────────────────────────────────────────────────────
  /**
   * Updates a string field in the form state
   * @param {any} name - field key
   * @param {any} value - new value
   */
  const handleField = (name: string, value: string | number | string[]) => {
    const decimalFields = ['ratedPowerOutput', 'maxVoltage', 'maxCurrent'];
    if (decimalFields.includes(name)) {
      const inputValue = value.toString();

      const regex = name === 'ratedPowerOutput' ? /^\d*\.?\d{0,3}$/ : /^\d*\.?\d{0,2}$/;
      if (!regex.test(inputValue)) {
        return;
      }
    }
    setForm((previous) => {
      const updatedForm = { ...previous, [name]: value };
      // Clear model if brand changes
      if (name === 'brand') {
        updatedForm.model = '';
      }
      // If currentType changes and is not hybrid, replicate to all connectors
      if (name === 'currentType') {
        if (value !== 'hybrid') {
          updatedForm.connectors = updatedForm.connectors.map((c) => ({
            ...c,
            currentType: value as string,
          }));
        }
      }
      return updatedForm;
    });

    // Clear validation error when field is updated
    setErrors((previousErrors) => {
      if (previousErrors[name] || (name === 'brand' && previousErrors.model)) {
        const nextErrors = { ...previousErrors };
        delete nextErrors[name];
        if (name === 'brand') {
          delete nextErrors.model;
        }
        return nextErrors;
      }
      return previousErrors;
    });
  };

  /**
   * Toggles a protocol chip on/off
   * @param {any} protocol - protocol string
   */
  const toggleProtocol = (protocol: string) => {
    setForm((previousState) => {
      const nextProtocol = previousState.protocols === protocol ? '' : protocol;

      setErrors((previousErrors) => {
        if (previousErrors.protocols) {
          const nextErrors = { ...previousErrors };
          delete nextErrors.protocols;
          return nextErrors;
        }
        return previousErrors;
      });

      return {
        ...previousState,
        protocols: nextProtocol,
      };
    });
  };

  /**
   * Toggles a certification chip on/off
   * @param {any} cert - certification label
   */
  const toggleCert = (cert: string) => {
    setForm((previous) => ({
      ...previous,
      certifications: previous.certifications.includes(cert)
        ? previous.certifications.filter((certItem) => certItem !== cert)
        : [...previous.certifications, cert],
    }));
  };

  // ── ID Tag helpers ────────────────────────────────────────────────────────────
  /**
   * Commits whatever is typed in the ID tag box as a chip. Blank values and
   * duplicates are dropped so the list stays clean.
   */
  const commitIdTagDraft = () => {
    const tag = idTagDraft.trim();
    if (!tag) return;

    setIdTagDraft('');
    if (form.idTag.includes(tag)) return;

    setForm((previous) => ({ ...previous, idTag: [...previous.idTag, tag] }));
    setErrors((previousErrors) => {
      if (previousErrors.plugAndChargeRequirement) {
        const nextErrors = { ...previousErrors };
        delete nextErrors.plugAndChargeRequirement;
        return nextErrors;
      }
      return previousErrors;
    });
  };

  /**
   * Removes a committed ID tag chip by its index
   * @param {number} index - Index of the ID tag entry to remove
   */
  const removeIdTag = (index: number) => {
    setForm((previous) => ({
      ...previous,
      idTag: previous.idTag.filter((_, tagIndex) => tagIndex !== index),
    }));
  };

  // ── Plug & Charge params helpers ─────────────────────────────────────────────
  /**
   * Appends a new blank plugAndChargeParams row, up to the 20-row limit
   */
  const addPlugAndChargeParameter = () => {
    if (form.plugAndChargeParams.length >= 20) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: t('maxPlugAndChargeParamsError'),
          code: 0,
        }),
      );
      return;
    }
    setForm((previous) => ({
      ...previous,
      plugAndChargeParams: [...previous.plugAndChargeParams, initialPlugAndChargeParameter()],
    }));
  };

  /**
   * Removes a plugAndChargeParams row by id
   * @param {number} rowId - Row id to remove
   */
  const removePlugAndChargeParameter = (rowId: number) => {
    setForm((previous) => ({
      ...previous,
      plugAndChargeParams: previous.plugAndChargeParams.filter(
        (parameter) => parameter.id !== rowId,
      ),
    }));
  };

  /**
   * Updates a field within a specific plugAndChargeParams row. Changing valueType
   * resets the row's value to a sane default for the newly selected type.
   * @param {number} rowId - Row id to update
   * @param {'key' | 'valueType' | 'value'} field - Field key within the row
   * @param {string | number | boolean} value - New value for the field
   */
  const updatePlugAndChargeParameter = (
    rowId: number,
    field: 'key' | 'valueType' | 'value',
    value: string | number | boolean,
  ) => {
    setForm((previous) => ({
      ...previous,
      plugAndChargeParams: previous.plugAndChargeParams.map((parameter) => {
        if (parameter.id !== rowId) return parameter;
        if (field === 'valueType') {
          return {
            ...parameter,
            valueType: value as 'string' | 'number' | 'boolean',
            value: value === 'boolean' ? false : '',
          };
        }
        return { ...parameter, [field]: value };
      }),
    }));

    setErrors((previousErrors) => {
      const nextErrors = { ...previousErrors };
      let changed = false;
      if (field === 'key' && nextErrors[`paramKey-${rowId}`]) {
        delete nextErrors[`paramKey-${rowId}`];
        changed = true;
      }
      if (field === 'value' && nextErrors[`paramValue-${rowId}`]) {
        delete nextErrors[`paramValue-${rowId}`];
        changed = true;
      }
      if (nextErrors.plugAndChargeRequirement) {
        delete nextErrors.plugAndChargeRequirement;
        changed = true;
      }
      return changed ? nextErrors : previousErrors;
    });
  };

  // ── Connector helpers ──────────────────────────────────────────────────────────
  /**
   * Adds a new blank connector row
   */
  const addConnector = () => {
    const totalConnectors = form.connectors.reduce((sum, connector) => sum + connector.quantity, 0);

    if (totalConnectors >= 4) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: t('limitConnectorError'),
          code: 0,
        }),
      );
      return;
    }

    setForm((previous) => {
      const newConnector = initialConnector();
      if (previous.currentType && previous.currentType !== 'hybrid') {
        newConnector.currentType = previous.currentType;
      }
      return {
        ...previous,
        connectors: [...previous.connectors, newConnector],
      };
    });
  };

  /**
   * Removes a connector row by id
   * @param {any} connectorId - row id
   */
  const removeConnector = (connectorId: number) => {
    setForm((previous) => ({
      ...previous,
      connectors: previous.connectors.filter((connector) => connector.id !== connectorId),
    }));
  };

  /**
   * Updates a field inside a specific connector row
   * @param {any} connectorId - connector row id
   * @param {any} field - field key within the connector row
   * @param {any} value - new value
   */
  const updateConnector = (
    connectorId: number,
    field: keyof ConnectorRow,
    value: string | number,
  ) => {
    if (field === 'maxVoltage' || field === 'maxCurrent' || field === 'maxPower') {
      const inputValue = value.toString();

      const regex =
        field === 'maxCurrent' || field === 'maxPower' ? /^\d*\.?\d{0,3}$/ : /^\d*\.?\d{0,2}$/;

      if (!regex.test(inputValue)) {
        return;
      }
    }
    setForm((previous) => ({
      ...previous,
      connectors: previous.connectors.map((connector) =>
        connector.id === connectorId ? { ...connector, [field]: value } : connector,
      ),
    }));

    // Clear error for this specific field if it exists
    const errorKey = `${field}-${connectorId}`;
    setErrors((previousErrors) => {
      if (previousErrors[errorKey]) {
        const nextErrors = { ...previousErrors };
        delete nextErrors[errorKey];
        return nextErrors;
      }
      return previousErrors;
    });
  };

  /**
   * Increments connector quantity by 1
   * @param {any} connectorId - connector row id
   */
  const incrementQty = (connectorId: number) => {
    const totalConnectors = form.connectors.reduce((sum, connector) => sum + connector.quantity, 0);

    if (totalConnectors >= 4) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: t('limitConnectorError'),
          code: 0,
        }),
      );
      return;
    }
    setForm((previous) => ({
      ...previous,
      connectors: previous.connectors.map((connector) =>
        connector.id === connectorId
          ? { ...connector, quantity: connector.quantity + 1 }
          : connector,
      ),
    }));
  };

  /**
   * Decrements connector quantity, minimum 1
   * @param {any} connectorId - connector row id
   */
  const decrementQty = (connectorId: number) => {
    setForm((previous) => ({
      ...previous,
      connectors: previous.connectors.map((connector) =>
        connector.id === connectorId && connector.quantity > 1
          ? { ...connector, quantity: connector.quantity - 1 }
          : connector,
      ),
    }));
  };

  /**
   * Returns the appropriate label for the save/update button based on state
   * @returns {string} The button label
   */
  const getSaveButtonLabel = () => {
    if (loading) return t('saving');
    return isEdit ? t('update') : t('save');
  };

  if (isEdit && loading) {
    return (
      <div className="loader">
        <BatteryLoader />
      </div>
    );
  }

  // Attached chargers (already linked to an owner) can only have their protocols and security
  // edited, through a separate restricted form and its own endpoint.
  if (isEdit && isAttachedDevice) {
    return (
      <AttachedDeviceEditForm
        deviceId={id || ''}
        brandName={rawDevice?.brandName || ''}
        modelName={rawDevice?.modelName || ''}
        serialNumber={rawDevice?.serialNumber || ''}
        firmwareProtocolTypes={
          Array.isArray(rawDevice?.firmwareProtocolTypes) ? rawDevice.firmwareProtocolTypes : []
        }
        diagnosticProtocolTypes={
          Array.isArray(rawDevice?.diagnosticProtocolTypes) ? rawDevice.diagnosticProtocolTypes : []
        }
        // security={deviceSecurity} — SECURITY PROFILES DISABLED, see AttachedDeviceEditForm.tsx
        onCancel={() => navigate(-1)}
        onSaved={() => navigate(-1)}
      />
    );
  }

  return (
    <div className="new-device-page-wrap">
      <div className="new-device-panel">
        {/* ── Top Bar ── */}
        <div className="new-device-topbar">
          <h2 className="new-device-title">{isEdit ? t('editDevice') : t('addDevice')}</h2>
          <div className="new-device-actions">
            <MyButton label={t('cancel')} buttonType="error" onClickFunc={() => navigate(-1)} />
            <MyButton
              label={getSaveButtonLabel()}
              buttonType="submit"
              onClickFunc={handleSave}
              disableButton={loading}
              loading={loading}
            />
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="new-device-body">
          {/* ━━━ Section 1: Device Type & Model ━━━ */}
          <div className="nd-section">
            <div className="nd-section-header">
              <div>
                <p className="nd-section-title">{t('deviceTypeAndModel')}</p>
                <p className="nd-section-subtitle">{t('deviceTypeAndModelDesc')}</p>
              </div>
              <div className="nd-header-buttons" style={{ display: 'flex', gap: '12px' }}>
                <MyButton
                  label={t('addBrand')}
                  buttonType="primary-outline"
                  onClickFunc={() => setBrandModalOpen(true)}
                  disableButton={!permissionCheck('devicesBrandsAdd')}
                  hoverLabel={!permissionCheck('devicesBrandsAdd') ? t('noBrandAddPermission') : ''}
                />
                <MyButton
                  label={t('addModel')}
                  buttonType="primary-outline"
                  onClickFunc={() => setModelModalOpen(true)}
                  disableButton={!permissionCheck('devicesModelsAdd')}
                  hoverLabel={!permissionCheck('devicesModelsAdd') ? t('noModelAddPermission') : ''}
                />
              </div>
            </div>

            <div className="nd-grid-4">
              <div className="nd-field">
                <span className="nd-label">
                  {t('deviceIdLabel')} <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">{t('deviceIdTooltip')}</span>
                  </span>
                </span>
                <CustomInput
                  type="text"
                  name="deviceId"
                  placeholder={t('enterDeviceId')}
                  value={form.deviceId}
                  onChangeValue={handleField}
                  error={!!errors.deviceId}
                  errorMessage={t('fieldRequiredError')}
                  valueRequired
                  characterLimit={100}
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  {t('brandLabel')} <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">{t('brandTooltip')}</span>
                  </span>
                </span>
                <SelectField
                  name="brand"
                  options={brandOptions}
                  placeholder={t('chooseBrand')}
                  selectedValue={form.brand}
                  onChangeValue={handleField}
                  error={!!errors.brand}
                  errorMessage={t('fieldRequiredError')}
                  valueRequired
                  enableSearch
                  hideLabelNodeInSelected
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  {t('modelLabel')} <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">{t('modelTooltip')}</span>
                  </span>
                </span>
                <SelectField
                  name="model"
                  options={modelOptions}
                  placeholder={t('chooseModel')}
                  selectedValue={form.model}
                  onChangeValue={handleField}
                  disabled={!form.brand}
                  error={!!errors.model}
                  errorMessage={t('fieldRequiredError')}
                  valueRequired
                  hideLabelNodeInSelected
                  loading={modelsLoading}
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  {t('currentType')} <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">{t('currentTypeTooltip')}</span>
                  </span>
                </span>
                <SelectField
                  name="currentType"
                  options={CURRENT_TYPE_OPTIONS}
                  placeholder={t('chooseCurrentType')}
                  selectedValue={form.currentType}
                  onChangeValue={handleField}
                  error={!!errors.currentType}
                  errorMessage={t('fieldRequiredError')}
                  valueRequired
                />
              </div>
            </div>

            <div className="nd-grid-4">
              <div className="nd-field">
                <span className="nd-label">
                  {t('skuNumberLabel')}
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">{t('skuNumberTooltip')}</span>
                  </span>
                </span>
                <CustomInput
                  type="text"
                  name="skuNumber"
                  placeholder="Eg: A8B-T360-CE"
                  value={form.skuNumber}
                  onChangeValue={handleField}
                  characterLimit={100}
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  {t('installationTypeLabel')}
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">{t('installationTypeTooltip')}</span>
                  </span>
                </span>
                <SelectField
                  name="installationType"
                  options={INSTALLATION_TYPE_OPTIONS}
                  placeholder={t('chooseInstallationType')}
                  selectedValue={form.installationType}
                  onChangeValue={handleField}
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  {t('firmwareVersionLabel')}
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">{t('firmwareVersionTooltip')}</span>
                  </span>
                </span>
                <CustomInput
                  type="text"
                  name="firmwareVersion"
                  placeholder={t('enterFirmwareVersion')}
                  value={form.firmwareVersion}
                  onChangeValue={handleField}
                  characterLimit={100}
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  {t('plugAndChargeLabel')} <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">{t('plugAndChargeTooltip')}</span>
                  </span>
                </span>
                <div className={`nd-radio-group${errors.plugAndCharge ? ' error' : ''}`}>
                  <label className="nd-radio-label" htmlFor="plug-yes">
                    <input
                      id="plug-yes"
                      type="radio"
                      name="plugAndCharge"
                      value="yes"
                      checked={form.plugAndCharge === 'yes'}
                      onChange={() => {
                        setForm((previous) => ({ ...previous, plugAndCharge: 'yes' }));
                        setErrors((previousErrors) => {
                          if (previousErrors.plugAndCharge) {
                            const nextErrors = { ...previousErrors };
                            delete nextErrors.plugAndCharge;
                            return nextErrors;
                          }
                          return previousErrors;
                        });
                      }}
                    />
                    {t('yes')}
                  </label>
                  <label className="nd-radio-label" htmlFor="plug-no">
                    <input
                      id="plug-no"
                      type="radio"
                      name="plugAndCharge"
                      value="no"
                      checked={form.plugAndCharge === 'no'}
                      onChange={() => {
                        setForm((previous) => ({ ...previous, plugAndCharge: 'no' }));
                        setErrors((previousErrors) => {
                          if (previousErrors.plugAndCharge) {
                            const nextErrors = { ...previousErrors };
                            delete nextErrors.plugAndCharge;
                            return nextErrors;
                          }
                          return previousErrors;
                        });
                      }}
                    />
                    {t('no')}
                  </label>
                </div>
              </div>
            </div>

            {form.plugAndCharge === 'yes' && (
              <div className="nd-plugcharge-section">
                {/* ID Tag list */}
                <div className="nd-field nd-idtag-field">
                  <span className="nd-label">
                    {t('idTagLabel')}
                    <span className="nd-col-tooltip-wrap">
                      <InfoIcon />
                      <span className="nd-col-tooltip-text">{t('idTagTooltip')}</span>
                    </span>
                  </span>

                  {/* Mirrors the parameter column header so both columns read as
                      parallel tables and their first rows share a baseline */}
                  <div className="nd-pnc-param-header nd-idtag-header" role="row">
                    <span role="columnheader">{t('idTagColumnHeader')}</span>
                  </div>

                  {/*
                    Reuses CustomInput's own custom-input-wrap/custom-input-container
                    class structure (verified in CustomInput.tsx) rather than the
                    component itself, because this field commits its value on Enter
                    and CustomInput exposes no key handler.
                  */}
                  <div className="custom-input-wrap small nd-idtag-input-wrap">
                    <div
                      className={`custom-input-container ${errors.plugAndChargeRequirement ? 'error' : ''}`}>
                      <input
                        type="text"
                        name="idTagDraft"
                        placeholder={t('enterIdTag')}
                        value={idTagDraft}
                        maxLength={100}
                        onChange={(event) => setIdTagDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            commitIdTagDraft();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="icon nd-idtag-add"
                        aria-label="Add ID Tag"
                        onClick={commitIdTagDraft}
                        disabled={idTagDraft.trim() === ''}>
                        +
                      </button>
                    </div>
                  </div>

                  {form.idTag.length > 0 && (
                    <div className="nd-idtag-chips">
                      {form.idTag.map((tag, index) => (
                        <span className="nd-idtag-chip" key={tag}>
                          {tag}
                          <button
                            type="button"
                            className="nd-chip-remove"
                            aria-label={`Remove ${tag}`}
                            onClick={() => removeIdTag(index)}>
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Plug & Charge Params */}
                <div className="nd-field nd-pnc-params-field">
                  <span className="nd-label">
                    {t('plugAndChargeParamsLabel')}
                    <span className="nd-col-tooltip-wrap">
                      <InfoIcon />
                      <span className="nd-col-tooltip-text">{t('plugAndChargeParamsTooltip')}</span>
                    </span>
                  </span>

                  <div className="nd-pnc-param-header" role="row">
                    <span role="columnheader">{t('paramKeyLabel')}</span>
                    <span role="columnheader">{t('paramValueTypeLabel')}</span>
                    <span role="columnheader">{t('paramValueLabel')}</span>
                    <span role="columnheader" aria-label="Actions" />
                  </div>

                  {form.plugAndChargeParams.map((parameter) => (
                    <div className="nd-pnc-param-row" key={parameter.id}>
                      <CustomInput
                        type="text"
                        name={`paramKey-${parameter.id}`}
                        placeholder={t('paramKeyPlaceholder')}
                        value={parameter.key}
                        onChangeValue={(_, value) =>
                          updatePlugAndChargeParameter(parameter.id, 'key', String(value))
                        }
                        characterLimit={100}
                        error={!!errors[`paramKey-${parameter.id}`]}
                        errorMessage={t('fieldRequiredError')}
                        fontSize="small"
                        hint={false}
                      />
                      <SelectField
                        name={`paramValueType-${parameter.id}`}
                        aria-label="Value Type"
                        options={VALUE_TYPE_OPTIONS}
                        placeholder={t('chooseValueType')}
                        selectedValue={parameter.valueType}
                        onChangeValue={(_, value) =>
                          updatePlugAndChargeParameter(parameter.id, 'valueType', value)
                        }
                      />
                      {parameter.valueType === 'boolean' ? (
                        <div className="nd-pnc-bool-value">
                          <SwitchButton
                            checked={parameter.value === true}
                            change={(checked) =>
                              updatePlugAndChargeParameter(parameter.id, 'value', checked)
                            }
                            fontSize="small"
                          />
                        </div>
                      ) : (
                        <CustomInput
                          type={parameter.valueType === 'number' ? 'number' : 'text'}
                          name={`paramValue-${parameter.id}`}
                          placeholder={t('paramValuePlaceholder')}
                          value={parameter.value as string | number}
                          onChangeValue={(_, value) =>
                            updatePlugAndChargeParameter(parameter.id, 'value', value)
                          }
                          error={!!errors[`paramValue-${parameter.id}`]}
                          errorMessage={t('fieldRequiredError')}
                          characterLimit={parameter.valueType === 'string' ? 200 : undefined}
                          fontSize="small"
                          hint={false}
                        />
                      )}
                      <button
                        type="button"
                        className="nd-icon-btn-ghost"
                        aria-label="Remove Parameter"
                        onClick={() => removePlugAndChargeParameter(parameter.id)}>
                        ×
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="nd-link-btn"
                    onClick={addPlugAndChargeParameter}
                    disabled={form.plugAndChargeParams.length >= 20}>
                    {t('addPlugAndChargeParam')}
                  </button>
                </div>

                {errors.plugAndChargeRequirement && (
                  <p className="nd-pnc-requirement-error">{t('plugAndChargeValidationError')}</p>
                )}
              </div>
            )}

            <div className="nd-connectivity-protocol-cert-row">
              <div className="nd-field">
                <CheckBoxButtons
                  inputLabel={t('networkConnectivity')}
                  name="networkConnectivity"
                  list={NETWORK_OPTIONS}
                  value={form.networkConnectivity}
                  onChangeValue={(name, value) => handleField(name, value as string[])}
                  error={!!errors.networkConnectivity}
                  errorMessage={t('fieldRequiredError')}
                  valueRequired
                />
              </div>
              {/* Protocol Support */}
              <div className="nd-field">
                <p className="nd-label">
                  {t('protocolSupport')} <span className="required">*</span>
                </p>

                <div
                  className={`nd-chip-group${errors.protocols ? ' error' : ''}`}
                  role="group"
                  aria-label="Protocol Support Selection">
                  {PROTOCOL_OPTIONS.map((protocol) => (
                    <button
                      key={protocol}
                      type="button"
                      className={`nd-chip${form.protocols === protocol ? ' active' : ''}`}
                      onClick={() => toggleProtocol(protocol)}>
                      <ProtocolIcon />
                      {protocol}
                    </button>
                  ))}
                </div>
              </div>

              <div className="nd-field">
                <p className="nd-label">{t('certifications')}</p>
                <div className="nd-chip-group">
                  {CERTIFICATIONS.map((cert) => (
                    <button
                      key={cert}
                      type="button"
                      className={`nd-chip nd-chip-cert${form.certifications.includes(cert) ? ' active' : ''}`}
                      onClick={() => toggleCert(cert)}>
                      <CertIcon />
                      {cert}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="nd-connectivity-protocol-cert-row" style={{ marginTop: '24px' }}>
              <div className="nd-field">
                <CheckBoxButtons
                  inputLabel={t('diagnosticProtocolTypes', { defaultValue: 'Diagnostic Protocol Types' })}
                  name="diagnosticProtocolTypes"
                  list={TRANSFER_PROTOCOL_OPTIONS}
                  value={form.diagnosticProtocolTypes}
                  onChangeValue={(name, value) => handleField(name, value as string[])}
                  valueRequired={!isEdit}
                  error={errors.diagnosticProtocolTypes}
                  errorMessage={t('diagnosticProtocolTypesRequired')}
                />
                {isEdit && form.diagnosticProtocolTypes.length === 0 && (
                  <p className="sp-helper-text">{protocolHint(modelDiagnosticProtocols)}</p>
                )}
              </div>
              <div className="nd-field">
                <CheckBoxButtons
                  inputLabel={t('firmwareProtocolTypes', { defaultValue: 'Firmware Protocol Types' })}
                  name="firmwareProtocolTypes"
                  list={TRANSFER_PROTOCOL_OPTIONS}
                  value={form.firmwareProtocolTypes}
                  onChangeValue={(name, value) => handleField(name, value as string[])}
                  valueRequired={!isEdit}
                  error={errors.firmwareProtocolTypes}
                  errorMessage={t('firmwareProtocolTypesRequired')}
                />
                {isEdit && form.firmwareProtocolTypes.length === 0 && (
                  <p className="sp-helper-text">{protocolHint(modelFirmwareProtocols)}</p>
                )}
              </div>
            </div>
          </div>

          {/* ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
              PR #1130/#1143). Re-enable by uncommenting these two blocks once backend security
              endpoints/fields ship. */}
          {/*
          <!-- ━━━ Security (create only) ━━━ -->
          {!isEdit && (
            <ChargerSecurityFields
              value={chargerSecurity}
              onChange={(next) => {
                setChargerSecurity(next);
                setErrors((previousErrors) => ({
                  ...previousErrors,
                  expectedSecurityProfile: false,
                  lowProfileReason: false,
                  credentialSource: false,
                  authorizationKey: false,
                }));
              }}
              allowedProfiles={allowedSecurityProfiles}
              modelSelected={Boolean(form.model) && modelCapabilityLoaded}
              modelNotProfiled={!modelCapability}
              noProfileAvailable={noSecurityProfileAvailable}
              errors={errors}
            />
          )}

          <!-- ━━━ Security (edit, unattached chargers only) ━━━ -->
          {isEdit && (
            <div className="nd-section">
              <div className="nd-section-header">
                <div>
                  <p className="nd-section-title">{t('security')}</p>
                  <p className="nd-section-subtitle">{t('securitySectionDesc')}</p>
                </div>
              </div>
              <SecurityEditFields
                security={deviceSecurity ?? {}}
                value={securityEditState}
                onChange={setSecurityEditState}
                showErrors={showSecurityEditErrors}
                errors={validateSecurityEditForm(deviceSecurity, securityEditState)}
                sectionDisabled={deviceSecurity?.transition?.state === 'pending'}
                showTransitionPendingNote={deviceSecurity?.transition?.state === 'pending'}
              />
            </div>
          )}
          */}

          {/* ━━━ Section 3: Specifications ━━━ */}
          <div className="nd-section">
            <div className="nd-section-header">
              <div>
                <p className="nd-section-title">{t('specifications')}</p>
                <p className="nd-section-subtitle">{t('specificationsDesc')}</p>
              </div>
            </div>

            <div className="nd-grid-4">
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="ratedPowerOutput"
                  inputLabel={t('powerOutputKW')}
                  placeholder="Eg: 12"
                  value={form.ratedPowerOutput}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg={t('max0To1000Allowed')}
                  decimalLimit={3}
                  error={!!errors.ratedPowerOutput}
                  errorMessage={t('fieldRequiredError')}
                  valueRequired
                />
              </div>
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="maxVoltage"
                  inputLabel={t('maxVoltageV')}
                  placeholder="Eg: 12"
                  value={form.maxVoltage}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg={t('max0To1000Allowed')}
                  decimalLimit={2}
                />
              </div>
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="maxCurrent"
                  inputLabel={t('maxCurrentA')}
                  placeholder="Eg: 32"
                  value={form.maxCurrent}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg={t('max0To1000Allowed')}
                  decimalLimit={2}
                />
              </div>
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="efficiencyRating"
                  inputLabel={t('efficiencyRating')}
                  placeholder="Eg: 95"
                  value={form.efficiencyRating}
                  onChangeValue={handleField}
                  maxValue={100}
                  limitErrorMsg={t('max1To100Allowed')}
                  decimalLimit={2}
                />
              </div>
            </div>

            <div className="nd-grid-4">
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="operationTemperature"
                  inputLabel={t('operationTemperature')}
                  placeholder={t('enterOperationTemperature')}
                  value={form.operationTemperature}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg={t('max0To1000Allowed')}
                  decimalLimit={2}
                />
              </div>
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="weight"
                  inputLabel={t('weightKg')}
                  placeholder="Eg: 12"
                  value={form.weight}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg={t('max0To1000Allowed')}
                  decimalLimit={2}
                />
              </div>
              <div className="nd-field">
                <SelectField
                  name="displayType"
                  inputLabel={t('displayType')}
                  options={DISPLAY_TYPE_OPTIONS}
                  placeholder={t('chooseDisplayType')}
                  selectedValue={form.displayType}
                  onChangeValue={handleField}
                />
              </div>
            </div>
          </div>

          {/* ━━━ Section 2: Connector Information ━━━ */}
          <div className="nd-section">
            <div className="nd-section-header">
              <div>
                <p className="nd-section-title">
                  {t('connectorInfo')} <span style={{ color: '#ff4d4f' }}>*</span>
                </p>
                <p className="nd-section-subtitle">{t('connectorInfoDesc')}</p>
              </div>
              <div className="nd-header-buttons">
                <MyButton
                  label={t('addConnectorRating')}
                  buttonType="primary-outline"
                  onClickFunc={() => setRatingModalOpen(true)}
                  disableButton={!permissionCheck('devicesRatingAdd')}
                  hoverLabel={
                    !permissionCheck('devicesRatingAdd') ? t('noRatingAddPermission') : ''
                  }
                />
              </div>
            </div>

            {/* Column headers */}
            <div className="nd-connector-header" role="row">
              <span role="columnheader">
                {t('connectorRatingLabel')} <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">{t('connectorRatingTooltip')}</span>
                </span>
              </span>
              <span role="columnheader">
                {t('currentType')} <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">{t('currentTypeDesc')}</span>
                </span>
              </span>

              <span role="columnheader">
                {t('maxPowerKW')}
                <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">{t('maxPowerTooltip')}</span>
                </span>
              </span>
              <span role="columnheader">
                {t('maxVoltageV')}
                <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">{t('maxVoltageTooltip')}</span>
                </span>
              </span>
              <span role="columnheader">
                {t('maxCurrentA')}
                <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">{t('maxCurrentTooltip')}</span>
                </span>
              </span>
              <span role="columnheader">
                {t('connectorTypeLabel')} <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">{t('connectorTypeTooltip')}</span>
                </span>
              </span>
              <span role="columnheader">
                {t('connectorFormat')} <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">{t('connectorFormatTooltip')}</span>
                </span>
              </span>

              <span role="columnheader" aria-label="Actions" />
            </div>

            {/* Connector rows */}
            {form.connectors.map((connector) => (
              <div key={connector.id} className="nd-connector-row">
                <SelectField
                  name={`connectorRating-${connector.id}`}
                  aria-label="Connector Rating"
                  options={connectorRatingOptions.filter((rating) => {
                    if (form.currentType === 'hybrid') {
                      return true;
                    }
                    return (
                      rating.currentType?.toLowerCase() === connector.currentType?.toLowerCase()
                    );
                  })}
                  placeholder={t('chooseRating')}
                  selectedValue={connector.connectorRating}
                  onChangeValue={(_, value) => {
                    updateConnector(connector.id, 'connectorRating', value);
                    const selectedRating = connectorRatingOptions.find(
                      (opt) => opt.value === value || opt.label === value,
                    );
                    if (selectedRating) {
                      if (selectedRating.capacityKW) {
                        updateConnector(connector.id, 'maxPower', selectedRating.capacityKW);
                      }
                      if (form.currentType === 'hybrid' && selectedRating.currentType) {
                        updateConnector(
                          connector.id,
                          'currentType',
                          selectedRating.currentType.toLowerCase(),
                        );
                      }
                    }
                  }}
                  error={!!errors[`connectorRating-${connector.id}`]}
                  errorMessage={t('fieldRequiredError')}
                  hideLabelNodeInSelected
                />
                <div
                  className={`nd-read-only-box ${errors[`currentType-${connector.id}`] ? 'error' : ''}`}
                  aria-label="Current Type">
                  {connector.currentType ? connector.currentType.toUpperCase() : ' '}
                </div>

                <CustomInput
                  name={`maxPower-${connector.id}`}
                  type="number"
                  placeholder="Eg: 50"
                  value={connector.maxPower}
                  onChangeValue={() => {}}
                  error={!!errors[`maxPower-${connector.id}`]}
                  errorMessage={t('fieldRequiredError')}
                  fontSize="small"
                  hint={false}
                  svgIcon={
                    <span
                      className="suffix"
                      style={{ fontSize: '11px', color: '#9aa3b2', fontWeight: 600 }}>
                      kW
                    </span>
                  }
                  maxValue={1000}
                  limitErrorMsg={t('max1To1000Allowed')}
                  decimalLimit={3}
                />
                <CustomInput
                  name={`maxVoltage-${connector.id}`}
                  type="number"
                  placeholder="Eg: 12"
                  value={connector.maxVoltage}
                  onChangeValue={(_, value) => updateConnector(connector.id, 'maxVoltage', value)}
                  error={!!errors[`maxVoltage-${connector.id}`]}
                  errorMessage={t('fieldRequiredError')}
                  fontSize="small"
                  hint={false}
                  svgIcon={
                    <span
                      className="suffix"
                      style={{ fontSize: '11px', color: '#9aa3b2', fontWeight: 600 }}>
                      V
                    </span>
                  }
                  maxValue={1000}
                  limitErrorMsg={t('max1To1000Allowed')}
                  decimalLimit={2}
                />
                <CustomInput
                  name={`maxCurrent-${connector.id}`}
                  type="number"
                  placeholder="Eg: 12"
                  value={connector.maxCurrent}
                  onChangeValue={(_, value) => updateConnector(connector.id, 'maxCurrent', value)}
                  error={!!errors[`maxCurrent-${connector.id}`]}
                  errorMessage={t('fieldRequiredError')}
                  fontSize="small"
                  hint={false}
                  svgIcon={
                    <span
                      className="suffix"
                      style={{ fontSize: '11px', color: '#9aa3b2', fontWeight: 600 }}>
                      A
                    </span>
                  }
                  maxValue={1000}
                  limitErrorMsg={t('max1To1000Allowed')}
                  decimalLimit={3}
                />
                <SelectField
                  name={`connectorType-${connector.id}`}
                  aria-label="Connector Type"
                  options={CONNECTOR_TYPE_OPTIONS}
                  placeholder={t('chooseConnectorType')}
                  selectedValue={connector.connectorType}
                  onChangeValue={(_, value) =>
                    updateConnector(connector.id, 'connectorType', value)
                  }
                  error={!!errors[`connectorType-${connector.id}`]}
                  errorMessage={t('fieldRequiredError')}
                />
                <SelectField
                  name={`connectorFormat-${connector.id}`}
                  aria-label="Connector Format"
                  options={CONNECTOR_FORMAT_OPTIONS}
                  placeholder={t('chooseFormat')}
                  selectedValue={connector.connectorFormat}
                  onChangeValue={(_, value) =>
                    updateConnector(connector.id, 'connectorFormat', value)
                  }
                  error={!!errors[`connectorFormat-${connector.id}`]}
                  errorMessage={t('fieldRequiredError')}
                />

                {/* Quantity controls + delete */}
                <div className="nd-qty-controls">
                  <button
                    type="button"
                    className="nd-qty-btn sub"
                    aria-label="Decrease Quantity"
                    onClick={() => decrementQty(connector.id)}>
                    −
                  </button>
                  <span className="nd-qty-val">{connector.quantity}</span>
                  <button
                    type="button"
                    className="nd-qty-btn add"
                    aria-label="Increase Quantity"
                    onClick={() => incrementQty(connector.id)}>
                    +
                  </button>
                  <button
                    type="button"
                    className="nd-qty-btn del"
                    aria-label="Delete Connector"
                    onClick={() => removeConnector(connector.id)}
                    disabled={form.connectors.length === 1}>
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}

            {/* Add Connector */}
            <div className="nd-add-connector">
              <button type="button" className="nd-link-btn" onClick={addConnector}>
                {t('addConnector')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. Re-enable alongside the
          Security section once backend security endpoints/fields ship.
      <OneTimeKeyModal
        authorizationKey={oneTimeKey}
        onConfirm={() => {
          setOneTimeKey(null);
          setShowSuccess(true);
        }}
      /> */}

      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title={isEdit ? t('deviceUpdatedSuccess') : t('deviceRegisteredSuccess')}
            description={isEdit ? t('deviceUpdatedSuccessDesc') : t('deviceRegisteredSuccessDesc')}
            onNotificationClose={() => navigate(-1)}
          />
        }
        closeModal={() => setShowSuccess(false)}
        header={false}
        notification
      />

      <AddBrandDrawer
        open={brandModalOpen}
        onClose={() => setBrandModalOpen(false)}
        onBrandCreated={(brandId) => {
          loadBrands().then(() => {
            if (brandId) {
              setForm((previous) => ({ ...previous, brand: brandId, model: '' }));
            }
          });
        }}
      />

      <AddModelDrawer
        open={modelModalOpen}
        onClose={() => setModelModalOpen(false)}
        onModelCreated={(brandId, modelId, models) => {
          setModelOptions(models);
          setForm((previous) => ({ ...previous, brand: brandId, model: modelId }));
        }}
      />

      <AddConnectorRatingDrawer
        open={ratingModalOpen}
        onClose={() => setRatingModalOpen(false)}
        existingValues={connectorRatingOptions.map((opt) => opt.value)}
        onRatingCreated={(ratingId) => {
          loadConnectorRatings().then((ratings) => {
            if (ratingId) {
              const createdRating = ratings.find((r) => r.value === ratingId);
              if (createdRating) {
                setForm((previous) => {
                  const targetIndex = previous.connectors.findIndex(
                    (c) =>
                      !c.connectorRating &&
                      (!c.currentType ||
                        !createdRating.currentType ||
                        c.currentType.toLowerCase() === createdRating.currentType.toLowerCase() ||
                        previous.currentType === 'hybrid'),
                  );

                  const indexToUpdate = targetIndex !== -1 ? targetIndex : 0;

                  const updatedConnectors = previous.connectors.map((c, index) => {
                    if (index === indexToUpdate) {
                      return {
                        ...c,
                        connectorRating: createdRating.value,
                        maxPower: createdRating.capacityKW || c.maxPower,
                        currentType:
                          previous.currentType === 'hybrid' && createdRating.currentType
                            ? createdRating.currentType.toLowerCase()
                            : c.currentType || createdRating.currentType?.toLowerCase() || '',
                      };
                    }
                    return c;
                  });

                  return {
                    ...previous,
                    connectors: updatedConnectors,
                  };
                });

                setErrors((previousErrors) => {
                  const nextErrors = { ...previousErrors };
                  form.connectors.forEach((c) => {
                    delete nextErrors[`connectorRating-${c.id}`];
                    delete nextErrors[`maxPower-${c.id}`];
                    delete nextErrors[`currentType-${c.id}`];
                  });
                  return nextErrors;
                });
              }
            }
          });
        }}
      />
    </div>
  );
}

export default AddDevice;
